import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType,
  BrowserCacheLocation,
  LogLevel,
} from '@azure/msal-browser';
import {
  MsalInterceptor,
  MSAL_INSTANCE,
  MsalInterceptorConfiguration,
  MsalGuardConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
} from '@azure/msal-angular';
import { environment } from '../../../environments/environment';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { lastValueFrom } from 'rxjs/internal/lastValueFrom';

/**
 * MSAL Instance Factory
 *
 * @export
 * @return {*}  {IPublicClientApplication}
 */
export const MSALInstanceFactory = (): IPublicClientApplication => {
  return new PublicClientApplication({
    auth: {
      clientId: environment.AUTH_CONFIG.clientId,
      authority: environment.AUTH_CONFIG.authority,
      redirectUri: environment.AUTH_CONFIG.redirectUri,
      postLogoutRedirectUri: environment.AUTH_CONFIG.postLogoutRedirectUri,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: false,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
};

/**
 * MSAL Interceptor Configuration Factory
 *
 * @export
 * @return {*}  {MsalInterceptorConfiguration}
 */
export const MSALInterceptorConfigFactory =
  (): MsalInterceptorConfiguration => {
    const protectedResourceMap = new Map<string, string[]>();
    protectedResourceMap.set(
      environment.API_CONFIG.uri,
      environment.API_CONFIG.tciu_scope,
    );
    protectedResourceMap.set(
      environment.APP_BASE_URL,
      environment.API_CONFIG.tciu_scope,
    );

    return {
      interactionType: InteractionType.Redirect,
      protectedResourceMap,
    };
  };

/**
 * // MSAL Guard Configuration Factory
 *
 * @export
 * @return {*}  {MsalGuardConfiguration}
 */
export const MSALGuardConfigFactory = (): MsalGuardConfiguration => {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.API_CONFIG.tciu_scope],
    },
    loginFailedRoute: '/login-failed',
  };
};
/**
 * MSAL Providers
 *
 * This object contains the providers for MSAL services, including the interceptor,
 * instance, guard configuration, and interceptor configuration.
 *
 * @export
 * @constant
 */
export const MSAL_PROVIDERS = {
  INTERCEPTOR: {
    provide: HTTP_INTERCEPTORS,
    useClass: MsalInterceptor,
    multi: true,
  },
  INSTANCE: {
    provide: MSAL_INSTANCE,
    useFactory: MSALInstanceFactory,
  },
  GUARD_CONFIG: {
    provide: MSAL_GUARD_CONFIG,
    useFactory: MSALGuardConfigFactory,
  },
  INTERCEPTOR_CONFIG: {
    provide: MSAL_INTERCEPTOR_CONFIG,
    useFactory: MSALInterceptorConfigFactory,
  },
  MSAL_SERVICE: [MsalService, MsalGuard, MsalBroadcastService],
};
export const msalInitializer = (
  msalService: MsalService,
): (() => Promise<void>) => {
  return async () => {
    await lastValueFrom(msalService.initialize());
  };
};
